import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { theme } from '../../theme/theme';
import { CloseIcon } from '../icons';

interface User {
  id: string;
  name: string;
  username: string;
  avatarUrl?: string;
}

interface CommentInputProps {
  currentUser?: User;
  placeholder?: string;
  replyTo?: {
    id: string;
    user: User;
  };
  onSubmit: (content: string, parentId?: string) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  maxLength?: number;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  currentUser,
  placeholder = '写下你的评论...',
  replyTo,
  onSubmit,
  onCancel,
  loading = false,
  maxLength = 1000,
}) => {
  const [content, setContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const handleSubmit = async () => {
    if (!content.trim() || loading) return;

    try {
      await onSubmit(content.trim(), replyTo?.id);
      setContent('');
      inputRef.current?.blur();
    } catch (error) {
      console.error('Submit comment error:', error);
    }
  };

  const handleCancel = () => {
    setContent('');
    setIsFocused(false);
    inputRef.current?.blur();
    onCancel?.();
  };

  const canSubmit = content.trim().length > 0 && !loading;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      {/* 回复提示 */}
      {replyTo && (
        <View style={styles.replyHeader}>
          <Text style={styles.replyText}>
            回复 <Text style={styles.replyUsername}>@{replyTo.user.name}</Text>
          </Text>
          <TouchableOpacity onPress={handleCancel} style={styles.cancelButton}>
            <CloseIcon size={16} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.inputContainer}>
        {/* 用户头像 */}
        {currentUser && (
          <Image
            source={{ 
              uri: currentUser.avatarUrl || 'https://via.placeholder.com/32x32?text=A' 
            }}
            style={styles.avatar}
          />
        )}

        {/* 输入框 */}
        <View style={styles.inputWrapper}>
          <TextInput
            ref={inputRef}
            style={[
              styles.input,
              isFocused && styles.inputFocused,
              !currentUser && styles.inputFullWidth,
            ]}
            value={content}
            onChangeText={setContent}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.textSecondary}
            multiline
            maxLength={maxLength}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            editable={!loading}
          />

          {/* 字符计数 */}
          {isFocused && (
            <Text style={styles.charCount}>
              {content.length}/{maxLength}
            </Text>
          )}
        </View>

        {/* 发送按钮 */}
        {(isFocused || content.length > 0) && (
          <TouchableOpacity
            style={[
              styles.sendButton,
              canSubmit && styles.sendButtonActive,
            ]}
            onPress={handleSubmit}
            disabled={!canSubmit}
          >
            <Text style={[
              styles.sendButtonText,
              canSubmit && styles.sendButtonTextActive,
            ]}>
              {loading ? '发送中...' : '发送'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.surface,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  replyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.colors.bg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  replyText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
  },
  replyUsername: {
    color: theme.colors.primary,
    fontWeight: theme.fontWeight.medium,
  },
  cancelButton: {
    padding: theme.spacing.xs,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: theme.spacing.sm,
  },
  inputWrapper: {
    flex: 1,
    position: 'relative',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.lg,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    backgroundColor: theme.colors.bg,
    maxHeight: 100,
    minHeight: 40,
  },
  inputFocused: {
    borderColor: theme.colors.primary,
    borderWidth: 2,
  },
  inputFullWidth: {
    marginRight: 0,
  },
  charCount: {
    position: 'absolute',
    bottom: -20,
    right: 0,
    fontSize: theme.fontSize.xs,
    color: theme.colors.textSecondary,
  },
  sendButton: {
    marginLeft: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.border,
  },
  sendButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  sendButtonText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
    fontWeight: theme.fontWeight.medium,
  },
  sendButtonTextActive: {
    color: 'white',
  },
});
